/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { Grid } from '@radix-ui/themes'
import {
  useUpdateCaseStudy,
  useUpdateQuestion,
} from '@/services/updateQuestionService'
import { useGetChapter } from '@/services/chapterService'
import { useModulesStore } from '@/stores/useModulesStore'
import {
  CaseStudyQuestionType,
  SubQuestion,
  MarkingSchemeType,
  UpdateQuestionBodyType,
  UpdateSubQuestion,
  QuestionDetailType,
} from '@/types/question.type'
import { toast } from 'sonner'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import QuestionHeader from '../QuestionHeader'
import { ChildQuestionContainer } from '../QuestionItemContainer'
import QuestionSchema from '../QuestionSchema'

interface CaseStudyQuestionDetailProps {
  questionDetail: CaseStudyQuestionType
  isLoading: boolean
  questionType: string
  fromClass: boolean
  refetchQuestionDetail: () => void
  onDeleteSuccess?: () => void
}

const CaseStudyQuestionDetail = ({
  fromClass,
  questionDetail,
  isLoading,
  questionType,
  refetchQuestionDetail,
  onDeleteSuccess,
}: CaseStudyQuestionDetailProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editableData, setEditableData] =
    useState<CaseStudyQuestionType | null>(null)

  const { updateQuestionAsync, isUpdating } = useUpdateQuestion()
  const { updateCaseStudyAsync, isUpdatingCaseStudy } = useUpdateCaseStudy()
  const { selectedModule } = useModulesStore()

  const { chapters } = useGetChapter({
    module_id: String(selectedModule?.id ?? ''),
    page: 1,
    limit: 100,
    search: '',
  })

  const isBlank = (value?: string) => (value ?? '').trim().length === 0

  const isInvalid = useMemo(() => {
    if (!isEditing) return false
    if (!editableData) return true

    // Check case study content
    if (isBlank(editableData.case_study)) {
      return true
    }

    // Check all questions and their sub-questions
    const questions = editableData.questions as unknown as QuestionDetailType[]
    for (const question of questions) {
      // Both texts are editable whatever the number of parts, so both must be filled in.
      if (isBlank(question.text)) {
        return true
      }
      if (
        question.sub_questions?.length === 1 &&
        isBlank(question.sub_questions[0].text)
      ) {
        return true
      }
      for (const sub of question.sub_questions) {
        // Validate indicative content per sub-question
        if (isBlank(sub.indicative_content)) {
          return true
        }
        // Validate time
        if (!sub.time_limit || sub.time_limit < 1) {
          return true
        }

        // Validate score; if marking scheme is entirely blank, only require marks >= 1
        const scheme = sub.marking_scheme || []
        const isEntryBlank = (s: {
          mark_range?: string
          description?: string
        }) => isBlank(s.mark_range) && isBlank(s.description)
        const isSchemeEntirelyBlank =
          scheme.length === 0 || scheme.every((s) => isEntryBlank(s))

        if (isSchemeEntirelyBlank) {
          if (!sub.marks || sub.marks < 1) {
            return true
          }
        } else {
          // Mark range validation is now optional
          const nonBlankByRange = scheme
            .filter(
              (s) =>
                !isBlank(s.mark_range) &&
                s.level !== null &&
                s.level !== undefined
            )
            .sort((a, b) => (a.level || 0) - (b.level || 0))

          if (nonBlankByRange.length > 0) {
            const last = nonBlankByRange[nonBlankByRange.length - 1]
            const value = (last.mark_range || '').trim()
            let maxAllowed = 0
            // Only allow min-max format, not single numbers
            const parts = value.split('-')
            if (parts.length === 2 && /^\d+$/.test(parts[1].trim())) {
              maxAllowed = parseInt(parts[1].trim(), 10)
            }

            // Only validate marks against marking scheme if mark range is provided and valid
            if (
              !sub.marks ||
              sub.marks < 1 ||
              (maxAllowed > 0 && sub.marks !== maxAllowed)
            ) {
              return true
            }
          } else {
            // No mark ranges provided, only validate minimum marks
            if (!sub.marks || sub.marks < 1) return true
          }
        }

        if (sub.marking_scheme && !isSchemeEntirelyBlank) {
          const filteredScheme = sub.marking_scheme.filter(
            (s) => !(isBlank(s.mark_range) && isBlank(s.description))
          )

          // Mark range is now optional - no validation required

          // Only validate levels that have mark ranges
          const sortedScheme = [...filteredScheme]
            .filter((s) => !isBlank(s.mark_range))
            .sort((a, b) => (a.level || 0) - (b.level || 0))

          // Validate individual level ranges only if mark ranges are provided
          for (const level of sortedScheme) {
            const raw = (level.mark_range || '').trim()
            // Only allow min-max format, not single numbers
            const parts = raw.split('-')
            if (parts.length !== 2) return true // Must be min-max format
            const minStr = parts[0].trim()
            const maxStr = parts[1].trim()
            if (!/^\d+$/.test(minStr) || !/^\d+$/.test(maxStr)) return true
            const min = parseInt(minStr, 10)
            const max = parseInt(maxStr, 10)
            if (min > max) return true
          }

          // Validate level ordering for min-max format only
          for (let i = 1; i < sortedScheme.length; i++) {
            const currentLevel = sortedScheme[i]
            const previousLevel = sortedScheme[i - 1]

            const cur = (currentLevel.mark_range || '').trim()
            const prev = (previousLevel.mark_range || '').trim()

            // Extract max from previous level (min-max format)
            const prevParts = prev.split('-')
            const prevMax =
              prevParts.length === 2 && /^\d+$/.test(prevParts[1].trim())
                ? parseInt(prevParts[1].trim(), 10)
                : NaN

            // Extract min from current level (min-max format)
            const curParts = cur.split('-')
            const curMin =
              curParts.length === 2 && /^\d+$/.test(curParts[0].trim())
                ? parseInt(curParts[0].trim(), 10)
                : NaN
            if (Number.isFinite(prevMax) && Number.isFinite(curMin)) {
              if ((curMin as number) < (prevMax as number)) {
                return true
              }
            }
          }
        }
      }
    }
    return false
  }, [editableData, isEditing])

  const cantEditMark = useMemo(() => {
    if (!questionDetail) {
      return true
    }

    return questionDetail.questions.every((item) => item.in_pt)
  }, [questionDetail])

  const handleSave = async () => {
    if (!editableData) return

    if (isInvalid) {
      const firstInvalid = document.querySelector(
        '[data-invalid="true"], [aria-invalid="true"]'
      ) as HTMLTextAreaElement | HTMLInputElement | null
      const container = (firstInvalid?.closest('[data-question-container]') ||
        firstInvalid?.closest('[data-scroll-target]')) as HTMLElement | null
      const target: HTMLElement | null = container || firstInvalid
      if (target) {
        // Open collapsible if present
        const trigger = target.querySelector(
          '[data-collapsible-trigger="true"]'
        ) as HTMLElement | null
        if (trigger) {
          trigger.click()
        }

        // Use start to avoid being hidden under sticky headers; rely on scroll-margin
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setTimeout(() => {
          if (firstInvalid && typeof firstInvalid.focus === 'function') {
            firstInvalid.focus()
          }
        }, 150)
      }
      toast.error('Please fix the highlighted fields before saving.', {
        duration: 3000,
        position: 'top-right',
      })
      return
    }

    try {
      // Update case study content
      await updateCaseStudyAsync({
        caseStudyId: questionDetail.case_study_id,
        content: editableData.case_study,
      })

      // Update each question in the case study
      for (const question of editableData.questions as unknown as QuestionDetailType[]) {
        const subQuestionsPayload: UpdateSubQuestion[] =
          question.sub_questions.map((subQuestion: SubQuestion) => ({
            indicative_content: subQuestion.indicative_content,
            id: subQuestion.sub_question_id,
            mark: subQuestion.marks,
            text: subQuestion.text,
            time_required: subQuestion.time_limit,
            chapter_id: subQuestion.chapter_id ?? undefined,
            options: (subQuestion.options || []).map(
              (opt: {
                option_id?: number
                id?: number
                text?: string
                option_text?: string
                is_correct?: boolean
              }) => ({
                id: opt.option_id ?? opt.id ?? 0,
                option_text: (opt.text ?? opt.option_text ?? '').toString(),
                is_correct: opt.is_correct,
              })
            ),
            update_marking_scheme: subQuestion.marking_scheme
              .filter((scheme: MarkingSchemeType) => {
                // Keep any entry that has mark_range, description, or rubric
                const hasMarkRange = (scheme.mark_range ?? '').trim().length > 0
                const hasDescription =
                  (scheme.description ?? '').trim().length > 0
                const hasRubric = (scheme.rubric ?? '').trim().length > 0
                return hasMarkRange || hasDescription || hasRubric
              })
              .map((scheme: MarkingSchemeType) => {
                const result: any = {
                  level: null,
                  min_mark: null,
                  max_mark: null,
                  descriptor: scheme.description,
                  rubric: scheme.rubric,
                }

                // Only set level and mark values if mark_range is provided
                if (scheme.mark_range && scheme.mark_range.trim().length > 0) {
                  result.level = scheme.level
                  const parts = scheme.mark_range.split('-')
                  if (parts.length === 2) {
                    result.min_mark = parseFloat(parts[0] ?? '0') || 0
                    result.max_mark = parseFloat(parts[1] ?? '0') || 0
                  }
                }

                if (scheme.marking_scheme_level_id) {
                  result.id = scheme.marking_scheme_level_id
                }

                return result
              }),
          }))

        const reqBody: UpdateQuestionBodyType = {
          // Always the question's own text. Substituting the sub-question's text when
          // there is exactly one part overwrote the scenario with the task on every save.
          text: question.text,

          sub_questions: subQuestionsPayload,
        }

        await updateQuestionAsync({
          questionId: question.question_id.toString(),
          data: reqBody,
        })
      }
      refetchQuestionDetail()

      toast.success('Case Study and questions updated successfully', {
        duration: 3000,
        position: 'top-right',
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating case study:', error)
      if (error instanceof AxiosError) {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
        console.log(error.response)
      } else {
        toast.error('An unexpected error occurred', {
          duration: 3000,
          position: 'top-right',
        })
      }
    }
  }

  const handleCancel = () => {
    // Reset editableData to the original questionDetail
    setEditableData(
      JSON.parse(JSON.stringify(questionDetail)) as CaseStudyQuestionType
    )
    setIsEditing(false)
  }

  // Initialize editableData when questionDetail changes
  useEffect(() => {
    if (questionDetail && !editableData) {
      setEditableData(
        JSON.parse(JSON.stringify(questionDetail)) as CaseStudyQuestionType
      )
    }
  }, [questionDetail, editableData])

  if (isLoading) {
    return (
      <Grid columns={'1'} gap={'4'} className='mt-3'>
        <Skeleton className='h-[200px] w-full bg-[#d6d6d6]' />
        <Skeleton className='h-[200px] w-full bg-[#d6d6d6]' />
      </Grid>
    )
  }

  return (
    <>
      <QuestionHeader
        section={editableData?.questions?.[0]?.section ?? ''}
        questionInfo={editableData?.questions?.[0]?.question_info ?? ''}
        isUpdating={isUpdating || isUpdatingCaseStudy}
        isInDetail={true}
        cantEdit={!fromClass}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        handleSave={handleSave}
        handleCancel={handleCancel}
        saveDisabled={isUpdatingCaseStudy || isUpdating}
        question_id={
          editableData?.questions?.[0]?.question_id ??
          questionDetail.questions[0].question_id
        }
        questionType={questionType}
        createdAt={editableData?.questions?.[0].created_at ?? ''}
        in_pt={editableData?.questions?.some((q) => q.in_pt)}
        onDeleteSuccess={onDeleteSuccess}
      />
      <div className='mt-3 grid h-fit grid-cols-1 gap-4'>
        {/* show the case study content and questions */}
        <Card className='h-fit border-none px-6 py-5 text-[#002652] shadow-none'>
          <div className='min-h-[300px] space-y-4'>
            {/* Case Study Content */}
            <div>
              {/* <h3 className='mb-2 font-semibold text-[#002652]'>Case Study:</h3> */}
              {isEditing && !cantEditMark ? (
                <textarea
                  className={`min-h-[100px] w-full resize-y rounded-md border p-2 outline-none ${
                    isBlank(editableData?.case_study)
                      ? 'border-red-500'
                      : 'border-[#C2DEFF]'
                  }`}
                  data-invalid={
                    isBlank(editableData?.case_study) ? 'true' : 'false'
                  }
                  rows={4}
                  value={editableData?.case_study ?? ''}
                  onChange={(e) =>
                    setEditableData((prev) => {
                      if (!prev) return prev
                      return {
                        ...prev,
                        case_study: e.target.value,
                      }
                    })
                  }
                />
              ) : (
                <p className='w-full whitespace-pre-wrap text-[#002652] select-text'>
                  {questionDetail.case_study ?? 'N/A'}
                </p>
              )}
            </div>

            {/* Individual Questions */}
            {editableData?.questions?.map(
              (question: unknown, questionIndex) => {
                const q = question as QuestionDetailType
                return (
                  <div
                    key={questionIndex}
                    className='scroll-mt-[120px] border-t pt-4'
                    data-question-container='true'
                  >
                    <h4 className='mb-2 font-medium text-[#002652]'>
                      Question {questionIndex + 1} ({q.moodle_question_id}):
                    </h4>

                    {/* The question's own text, as for a question with several parts. It
                        used to show the sub-question's text here, which repeated the task
                        already listed on the right and never showed the scenario. */}
                    {q.sub_questions?.length === 1 ? (
                      <div>
                        {isEditing && !cantEditMark ? (
                          <textarea
                            className={`min-h-[80px] w-full resize-y rounded-md border p-2 whitespace-pre-wrap outline-none ${
                              isBlank(q.text)
                                ? 'border-red-500'
                                : 'border-[#C2DEFF]'
                            }`}
                            data-invalid={isBlank(q.text) ? 'true' : 'false'}
                            rows={3}
                            value={q.text ?? ''}
                            onChange={(e) => {
                              setEditableData((prev) => {
                                if (!prev) return prev
                                const next = { ...prev }
                                next.questions[questionIndex] = {
                                  ...next.questions[questionIndex],
                                  text: e.target.value,
                                }
                                return next
                              })
                            }}
                          />
                        ) : (
                          <p className='w-full whitespace-pre-wrap text-[#002652] select-text'>
                            {q.text ?? 'N/A'}
                          </p>
                        )}
                      </div>
                    ) : (
                      // Multiple sub-questions, display main question text and sub-questions
                      <>
                        {isEditing && !cantEditMark ? (
                          <textarea
                            className={`min-h-[80px] w-full resize-y rounded-md border p-2 whitespace-pre-wrap outline-none ${
                              isBlank(q.text)
                                ? 'border-red-500'
                                : 'border-[#C2DEFF]'
                            }`}
                            data-invalid={isBlank(q.text) ? 'true' : 'false'}
                            rows={3}
                            value={q.text}
                            onChange={(e) => {
                              setEditableData((prev) => {
                                if (!prev) return prev
                                const next = { ...prev }
                                next.questions[questionIndex] = {
                                  ...next.questions[questionIndex],
                                  text: e.target.value,
                                }
                                return next
                              })
                            }}
                          />
                        ) : (
                          <p className='w-full whitespace-pre-wrap text-[#002652] select-text'>
                            {q.text ?? 'N/A'}
                          </p>
                        )}

                        {/* Multiple sub-questions, display with indentation */}
                        {q.sub_questions?.map(
                          (subQuestion: SubQuestion, subIndex: number) => {
                            return (
                              <div
                                key={subIndex}
                                className='mt-3 ml-4 border-l-2 border-[#C2DEFF] pl-3'
                              >
                                {isEditing && !cantEditMark ? (
                                  <textarea
                                    className={`min-h-[60px] w-full resize-y rounded-md border p-2 whitespace-pre-wrap outline-none ${
                                      isBlank(subQuestion.text)
                                        ? 'border-red-500'
                                        : 'border-[#C2DEFF]'
                                    }`}
                                    data-invalid={
                                      isBlank(subQuestion.text)
                                        ? 'true'
                                        : 'false'
                                    }
                                    rows={2}
                                    value={subQuestion.text}
                                    onChange={(e) => {
                                      setEditableData((prev) => {
                                        if (!prev) return prev
                                        const next = { ...prev }
                                        next.questions[
                                          questionIndex
                                        ].sub_questions[subIndex] = {
                                          ...next.questions[questionIndex]
                                            .sub_questions[subIndex],
                                          text: e.target.value,
                                        }
                                        return next
                                      })
                                    }}
                                  />
                                ) : (
                                  <p className='w-full text-sm whitespace-pre-wrap text-[#002652] select-text'>
                                    {subQuestion.text ?? 'N/A'}
                                  </p>
                                )}
                              </div>
                            )
                          }
                        )}
                      </>
                    )}
                  </div>
                )
              }
            )}
          </div>
        </Card>

        {/* list all sub-questions from all nested questions */}
        <div className='flex flex-col gap-3'>
          <p className='pl-2.5 font-medium text-[#002652]'>
            Read the passage carefully and answer the following questions.
          </p>
          {editableData?.questions
            ?.map((question, questionIndex) => {
              const q = question as unknown as QuestionDetailType
              return q.sub_questions?.length === 1 ? (
                // If only one sub-question, display it as the main question
                <ChildQuestionContainer
                  chapter={q.sub_questions?.[0]?.chapters?.[0] ?? ''}
                  key={questionIndex}
                  title={`${q.moodle_question_id}`}
                  triggerContent={
                    <div
                      onMouseDown={
                        isEditing ? (e) => e.stopPropagation() : undefined
                      }
                      onClick={
                        isEditing ? (e) => e.stopPropagation() : undefined
                      }
                      onFocus={
                        isEditing ? (e) => e.stopPropagation() : undefined
                      }
                      onKeyDown={
                        isEditing ? (e) => e.stopPropagation() : undefined
                      }
                    >
                      {isEditing && !cantEditMark ? (
                        <textarea
                          className='mt-2 min-h-[80px] w-full resize-y rounded-md border border-[#C2DEFF] p-2 whitespace-pre-wrap text-[#002652] outline-none'
                          rows={3}
                          value={q.sub_questions[0].text}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            setEditableData((prev) => {
                              if (!prev) return prev
                              const next = { ...prev }
                              next.questions[questionIndex].sub_questions[0] = {
                                ...next.questions[questionIndex]
                                  .sub_questions[0],
                                text: e.target.value,
                              }
                              return next
                            })
                          }}
                        />
                      ) : (
                        <p className='mt-2 w-full text-left whitespace-pre-wrap text-[#002652] select-text'>
                          {q.sub_questions[0].text ?? 'N/A'}
                        </p>
                      )}
                    </div>
                  }
                  content={
                    <QuestionSchema
                      isParagraph={false}
                      isEditing={isEditing}
                      cantEditMark={cantEditMark}
                      data={{
                        indicative_content:
                          q.sub_questions[0].indicative_content,
                        rubric: '',
                      }}
                      scheme={q.sub_questions[0].marking_scheme}
                      onChange={(updated: {
                        indicative_content?: string
                        scheme?: MarkingSchemeType[]
                      }) => {
                        setEditableData((prev) => {
                          if (!prev) return prev
                          const next = { ...prev }
                          const currentQuestion = next.questions[questionIndex]

                          if (updated.indicative_content !== undefined) {
                            currentQuestion.sub_questions[0] = {
                              ...currentQuestion.sub_questions[0],
                              indicative_content: updated.indicative_content,
                            }
                          }
                          if (updated.scheme) {
                            currentQuestion.sub_questions[0] = {
                              ...currentQuestion.sub_questions[0],
                              marking_scheme: updated.scheme,
                            }
                          }
                          return next
                        })
                      }}
                    />
                  }
                  time={question.sub_questions[0].time_limit}
                  score={q.sub_questions[0].marks}
                  isEditing={isEditing}
                  editableChapter={{
                    value: q.sub_questions[0].chapters ?? [],
                    chapterId: q.sub_questions[0].chapter_id,
                    onChange: (chapterId: number, chapterName: string) => {
                      setEditableData((prev) => {
                        if (!prev) return prev
                        const next = { ...prev }
                        next.questions[questionIndex].sub_questions[0] = {
                          ...next.questions[questionIndex].sub_questions[0],
                          chapters: [chapterName],
                          chapter_id: chapterId,
                        }
                        return next
                      })
                    },
                    options: chapters ?? [],
                    canEdit: !q.in_pt,
                  }}
                  editableTime={{
                    value: q.sub_questions[0].time_limit,
                    onChange: (value: number) =>
                      setEditableData((prev) => {
                        if (!prev) return prev
                        const next = { ...prev }
                        next.questions[questionIndex].sub_questions[0] = {
                          ...next.questions[questionIndex].sub_questions[0],
                          time_limit: value,
                        }
                        return next
                      }),
                    invalid:
                      !q.sub_questions[0].time_limit ||
                      q.sub_questions[0].time_limit < 1,
                    canEdit: !q.in_pt,
                  }}
                  editableScore={{
                    value: q.sub_questions[0].marks,
                    onChange: (value: number) =>
                      setEditableData((prev) => {
                        if (!prev) return prev
                        const next = { ...prev }
                        next.questions[questionIndex].sub_questions[0] = {
                          ...next.questions[questionIndex].sub_questions[0],
                          marks: value,
                        }
                        return next
                      }),
                    invalid: (() => {
                      const scheme = q.sub_questions[0].marking_scheme || []
                      const isEntryBlank = (s: {
                        mark_range?: string
                        description?: string
                      }) => isBlank(s.mark_range) && isBlank(s.description)
                      const isSchemeEntirelyBlank =
                        scheme.length === 0 ||
                        scheme.every((s) => isEntryBlank(s))
                      if (isSchemeEntirelyBlank) {
                        return q.sub_questions[0].marks < 1
                      }

                      const sortedScheme = [...scheme]
                        .filter(
                          (s) =>
                            !isBlank(s.mark_range) &&
                            s.level !== null &&
                            s.level !== undefined
                        )
                        .sort((a, b) => (a.level || 0) - (b.level || 0))

                      for (const level of sortedScheme) {
                        const value = (level.mark_range || '').trim()
                        const parts = value.split('-')
                        if (parts.length !== 2) return true
                        const minStr = parts[0].trim()
                        const maxStr = parts[1].trim()
                        if (!/^\d+$/.test(minStr) || !/^\d+$/.test(maxStr))
                          return true
                        const min = parseInt(minStr, 10)
                        const max = parseInt(maxStr, 10)
                        if (min > max) return true
                      }

                      const nonBlankByRange = [...scheme]
                        .filter(
                          (s) =>
                            !isBlank(s.mark_range) &&
                            s.level !== null &&
                            s.level !== undefined
                        )
                        .sort((a, b) => (a.level || 0) - (b.level || 0))
                      let maxAllowed = 0
                      if (nonBlankByRange.length > 0) {
                        const last = nonBlankByRange[nonBlankByRange.length - 1]
                        const v = (last.mark_range || '').trim()
                        const parts = v.split('-')
                        if (
                          parts.length === 2 &&
                          /^\d+$/.test(parts[1].trim())
                        ) {
                          maxAllowed = parseInt(parts[1].trim(), 10)
                        }
                      }

                      return (
                        q.sub_questions[0].marks < 1 ||
                        (maxAllowed > 0 &&
                          q.sub_questions[0].marks !== maxAllowed)
                      )
                    })(),
                    canEdit: !q.in_pt,
                  }}
                />
              ) : (
                // Multiple sub-questions, display each with letters
                q.sub_questions?.map(
                  (subQuestion: SubQuestion, subIndex: number) => {
                    const globalIndex = questionIndex * 100 + subIndex // Create unique index

                    return (
                      <ChildQuestionContainer
                        chapter={subQuestion?.chapters?.[0] ?? ''}
                        key={globalIndex}
                        title={`${q.moodle_question_id}`}
                        triggerContent={
                          <div
                            onMouseDown={
                              isEditing ? (e) => e.stopPropagation() : undefined
                            }
                            onClick={
                              isEditing ? (e) => e.stopPropagation() : undefined
                            }
                            onFocus={
                              isEditing ? (e) => e.stopPropagation() : undefined
                            }
                            onKeyDown={
                              isEditing ? (e) => e.stopPropagation() : undefined
                            }
                          >
                            {isEditing && !cantEditMark ? (
                              <textarea
                                className='mt-2 min-h-[80px] w-full resize-y rounded-md border border-[#C2DEFF] p-2 whitespace-pre-wrap text-[#002652] outline-none'
                                rows={3}
                                value={subQuestion.text}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  setEditableData((prev) => {
                                    if (!prev) return prev
                                    const next = { ...prev }
                                    next.questions[questionIndex].sub_questions[
                                      subIndex
                                    ] = {
                                      ...next.questions[questionIndex]
                                        .sub_questions[subIndex],
                                      text: e.target.value,
                                    }
                                    return next
                                  })
                                }}
                              />
                            ) : (
                              <p className='mt-2 w-full text-left whitespace-pre-wrap text-[#002652] select-text'>
                                <p>{q.text}</p>
                                {subQuestion.text ?? 'N/A'}
                              </p>
                            )}
                          </div>
                        }
                        content={
                          <QuestionSchema
                            isParagraph={false}
                            isEditing={isEditing}
                            cantEditMark={cantEditMark}
                            data={{
                              indicative_content:
                                subQuestion.indicative_content,
                              rubric: '',
                            }}
                            scheme={subQuestion.marking_scheme}
                            onChange={(updated: {
                              indicative_content?: string
                              scheme?: MarkingSchemeType[]
                            }) => {
                              setEditableData((prev) => {
                                if (!prev) return prev
                                const next = { ...prev }
                                const currentQuestion =
                                  next.questions[questionIndex]

                                if (updated.indicative_content !== undefined) {
                                  currentQuestion.sub_questions[subIndex] = {
                                    ...currentQuestion.sub_questions[subIndex],
                                    indicative_content:
                                      updated.indicative_content,
                                  }
                                }
                                if (updated.scheme) {
                                  currentQuestion.sub_questions[subIndex] = {
                                    ...currentQuestion.sub_questions[subIndex],
                                    marking_scheme: updated.scheme,
                                  }
                                }
                                return next
                              })
                            }}
                          />
                        }
                        time={subQuestion.time_limit}
                        score={subQuestion.marks}
                        isEditing={isEditing}
                        editableChapter={{
                          value: subQuestion.chapters ?? [],
                          chapterId: subQuestion.chapter_id,
                          onChange: (
                            chapterId: number,
                            chapterName: string
                          ) => {
                            setEditableData((prev) => {
                              if (!prev) return prev
                              const next = { ...prev }
                              next.questions[questionIndex].sub_questions[
                                subIndex
                              ] = {
                                ...next.questions[questionIndex].sub_questions[
                                  subIndex
                                ],
                                chapters: [chapterName],
                                chapter_id: chapterId,
                              }
                              return next
                            })
                          },
                          options: chapters ?? [],
                          canEdit: !q.in_pt,
                        }}
                        editableTime={{
                          value: subQuestion.time_limit,
                          onChange: (value: number) =>
                            setEditableData((prev) => {
                              if (!prev) return prev
                              const next = { ...prev }
                              next.questions[questionIndex].sub_questions[
                                subIndex
                              ] = {
                                ...next.questions[questionIndex].sub_questions[
                                  subIndex
                                ],
                                time_limit: value,
                              }
                              return next
                            }),
                          invalid:
                            !subQuestion.time_limit ||
                            subQuestion.time_limit < 1,
                          canEdit: !q.in_pt,
                        }}
                        editableScore={{
                          value: subQuestion.marks,
                          onChange: (value: number) =>
                            setEditableData((prev) => {
                              if (!prev) return prev
                              const next = { ...prev }
                              next.questions[questionIndex].sub_questions[
                                subIndex
                              ] = {
                                ...next.questions[questionIndex].sub_questions[
                                  subIndex
                                ],
                                marks: value,
                              }
                              return next
                            }),
                          invalid: (() => {
                            const scheme = subQuestion.marking_scheme || []
                            const isEntryBlank = (s: {
                              mark_range?: string
                              description?: string
                            }) =>
                              isBlank(s.mark_range) && isBlank(s.description)
                            const isSchemeEntirelyBlank =
                              scheme.length === 0 ||
                              scheme.every((s) => isEntryBlank(s))
                            if (isSchemeEntirelyBlank) {
                              return subQuestion.marks < 1
                            }

                            // Mark range is now optional - no validation required

                            const sortedScheme = [...scheme]
                              .filter(
                                (s) =>
                                  !isBlank(s.mark_range) &&
                                  s.level !== null &&
                                  s.level !== undefined
                              )
                              .sort((a, b) => (a.level || 0) - (b.level || 0))

                            // Validate ranges and ordering similar to scheme validator
                            for (const level of sortedScheme) {
                              const value = (level.mark_range || '').trim()
                              // Only allow min-max format, not single numbers
                              const parts = value.split('-')
                              if (parts.length !== 2) return true // Must be min-max format
                              const minStr = parts[0].trim()
                              const maxStr = parts[1].trim()
                              if (
                                !/^\d+$/.test(minStr) ||
                                !/^\d+$/.test(maxStr)
                              )
                                return true
                              const min = parseInt(minStr, 10)
                              const max = parseInt(maxStr, 10)
                              if (min > max) return true
                            }

                            // Compute maxAllowed from the last remaining non-blank level's mark_range
                            const nonBlankByRange = [...scheme]
                              .filter(
                                (s) =>
                                  !isBlank(s.mark_range) &&
                                  s.level !== null &&
                                  s.level !== undefined
                              )
                              .sort((a, b) => (a.level || 0) - (b.level || 0))
                            let maxAllowed = 0
                            if (nonBlankByRange.length > 0) {
                              const last =
                                nonBlankByRange[nonBlankByRange.length - 1]
                              const v = (last.mark_range || '').trim()
                              // Only allow min-max format, not single numbers
                              const parts = v.split('-')
                              if (
                                parts.length === 2 &&
                                /^\d+$/.test(parts[1].trim())
                              ) {
                                maxAllowed = parseInt(parts[1].trim(), 10)
                              }
                            }

                            return (
                              subQuestion.marks < 1 ||
                              (maxAllowed > 0 &&
                                subQuestion.marks !== maxAllowed)
                            )
                          })(),
                          canEdit: !q.in_pt,
                        }}
                      />
                    )
                  }
                )
              )
            })
            .flat()}
        </div>
      </div>
    </>
  )
}

export default CaseStudyQuestionDetail
